import { MarkerType, type Edge, type Node } from "reactflow"

import {
  DEFAULT_MATCH_TYPE,
  type IVREdge,
  type IVRFlow,
  type IVRMatchType,
} from "@/types/IVRFlow"

// Re-export the contract guard + validator so existing component imports
// (`from "./ivr-flow-utils"`) keep working. The canonical implementation
// lives in @/types/IVRFlow so the server can import it without pulling in
// reactflow.
export { DEFAULT_MATCH_TYPE, isIVRFlow, validateIVRFlow } from "@/types/IVRFlow"

interface LayoutResult {
  positions: Record<string, { x: number; y: number }>
}

const layoutFlow = (flow: IVRFlow): LayoutResult => {
  const positions: Record<string, { x: number; y: number }> = {}
  const visited = new Set<string>()
  const horizontalSpacing = 260
  const verticalSpacing = 160

  // Precompute successors per node from auto_advance_to + outgoing edges.
  const successorsByNode = new Map<string, string[]>()
  for (const node of flow.nodes) {
    successorsByNode.set(node.id, [])
  }
  for (const node of flow.nodes) {
    if (node.auto_advance_to) {
      successorsByNode.get(node.id)?.push(node.auto_advance_to)
    }
  }
  for (const edge of flow.edges) {
    successorsByNode.get(edge.source)?.push(edge.destination)
  }

  const levels: string[][] = []
  let frontier = [flow.start_node].filter((id) =>
    flow.nodes.some((n) => n.id === id),
  )

  while (frontier.length > 0) {
    levels.push(frontier)
    frontier.forEach((id) => visited.add(id))
    const nextFrontier: string[] = []
    for (const id of frontier) {
      const successors = successorsByNode.get(id) ?? []
      for (const next of successors) {
        if (!visited.has(next) && !nextFrontier.includes(next)) {
          nextFrontier.push(next)
        }
      }
    }
    frontier = nextFrontier
  }

  const placed = new Set<string>()
  levels.forEach((level, depth) => {
    const offset = -((level.length - 1) * horizontalSpacing) / 2
    level.forEach((id, idx) => {
      positions[id] = {
        x: offset + idx * horizontalSpacing,
        y: depth * verticalSpacing,
      }
      placed.add(id)
    })
  })

  // Place orphan nodes below the rest
  const orphanRow = levels.length
  let orphanIdx = 0
  for (const node of flow.nodes) {
    if (!placed.has(node.id)) {
      positions[node.id] = {
        x: orphanIdx * horizontalSpacing,
        y: orphanRow * verticalSpacing,
      }
      orphanIdx += 1
    }
  }

  return { positions }
}

const edgeLabel = (edge: IVREdge): string => {
  const matchType = edge.match_type ?? DEFAULT_MATCH_TYPE
  if (matchType === "regex") {
    return `/${edge.input}/`
  }
  if (matchType === "fuzzy") {
    return `≈ ${edge.input}`
  }
  return edge.input
}

export const flowToReactFlow = (
  flow: IVRFlow,
  positionOverrides: Record<string, { x: number; y: number }> = {},
): { nodes: Node[]; edges: Edge[] } => {
  const { positions } = layoutFlow(flow)

  // Precompute whether each node has outgoing edges for the decision badge.
  const hasOutgoing = new Set(flow.edges.map((e) => e.source))

  const nodes: Node[] = flow.nodes.map((node) => {
    let nodeType: "start" | "terminal" | "decision" | "process" = "process"
    if (node.id === flow.start_node) {
      nodeType = "start"
    } else if (node.terminal) {
      nodeType = "terminal"
    } else if (hasOutgoing.has(node.id)) {
      nodeType = "decision"
    }

    return {
      id: node.id,
      type: nodeType,
      position: positionOverrides[node.id] ??
        positions[node.id] ?? { x: 0, y: 0 },
      data: {
        id: node.id,
        prompt: node.prompt,
        terminal: node.terminal,
        hasBranches: hasOutgoing.has(node.id),
        autoAdvance: !!node.auto_advance_to,
      },
    }
  })

  const edges: Edge[] = []
  for (const node of flow.nodes) {
    if (node.auto_advance_to) {
      edges.push({
        id: `auto:${node.id}->${node.auto_advance_to}`,
        source: node.id,
        target: node.auto_advance_to,
        label: "auto",
        markerEnd: { type: MarkerType.ArrowClosed },
      })
    }
  }
  flow.edges.forEach((edge, idx) => {
    edges.push({
      id: `edge:${idx}:${edge.source}->${edge.destination}`,
      source: edge.source,
      target: edge.destination,
      label: edgeLabel(edge),
      markerEnd: { type: MarkerType.ArrowClosed },
    })
  })

  return { nodes, edges }
}

interface IVRFlowPath {
  /** Ordered list of node ids walked from start_node to a terminal node. */
  nodeIds: string[]
  /** Concrete caller inputs to traverse this path, in order. Regex edges are
   * materialized to random digits matching the pattern (e.g. `\d{4}` → 4 digits)
   * so the script can be replayed verbatim. Auto-advance steps add nothing
   * because they don't wait for input. */
  scriptTurns: string[]
}

/** Random digit string of the given length. Each generation yields a fresh
 * sample (good IVR test coverage — re-running the generator produces a new
 * set of script values). Generated values are persisted on the test case,
 * so re-running the simulation against the saved test set is deterministic. */
const randomDigits = (length: number): string => {
  let out = ""
  for (let i = 0; i < length; i++) {
    out += String(Math.floor(Math.random() * 10))
  }
  return out
}

/** Turn an edge's `input` into the concrete string a caller would actually
 * send. Literal/fuzzy edges pass through; regex edges are pattern-matched
 * against common IVR shapes (\d{n}, \d{n,m}, \d+, \d*, \d) and generated.
 * Patterns we don't recognize fall through unchanged — the user can then
 * hand-edit the resulting script turn. */
const materializeEdgeInput = (
  input: string,
  matchType: IVRMatchType = DEFAULT_MATCH_TYPE,
): string => {
  if (matchType !== "regex") {
    return input
  }
  // \d{n}
  const fixed = input.match(/^\\d\{(\d+)\}$/)
  if (fixed) {
    return randomDigits(Number(fixed[1]))
  }
  // \d{n,m} — generate the minimum length
  const ranged = input.match(/^\\d\{(\d+),(\d+)\}$/)
  if (ranged) {
    return randomDigits(Number(ranged[1]))
  }
  // \d+, \d* — pick a sensible default length
  if (input === "\\d+" || input === "\\d*") {
    return randomDigits(5)
  }
  // single \d
  if (input === "\\d") {
    return "1"
  }
  return input
}

/** Enumerate every distinct start-to-terminal path through the flow.
 *
 * Cycle protection: a path will not revisit a node it already contains.
 * This means loop-back edges (e.g. retry-on-failure) won't generate infinite
 * paths — each path is a simple walk. Bounded by `maxPaths` as a safety net
 * for fan-out heavy trees. */
export const enumerateFlowPaths = (
  flow: IVRFlow,
  maxPaths = 200,
): IVRFlowPath[] => {
  const nodesById = new Map(flow.nodes.map((n) => [n.id, n]))
  const edgesBySource = new Map<string, IVREdge[]>()
  for (const edge of flow.edges) {
    const list = edgesBySource.get(edge.source) ?? []
    list.push(edge)
    edgesBySource.set(edge.source, list)
  }

  const paths: IVRFlowPath[] = []

  const walk = (
    nodeId: string,
    visitedNodes: string[],
    scripts: string[],
  ): void => {
    if (paths.length >= maxPaths) {
      return
    }
    if (visitedNodes.includes(nodeId)) {
      return
    }
    const node = nodesById.get(nodeId)
    if (!node) {
      return
    }
    const nextNodes = [...visitedNodes, nodeId]

    if (node.terminal) {
      paths.push({ nodeIds: nextNodes, scriptTurns: [...scripts] })
      return
    }

    if (node.auto_advance_to) {
      walk(node.auto_advance_to, nextNodes, scripts)
      return
    }

    const outgoing = edgesBySource.get(nodeId) ?? []
    for (const edge of outgoing) {
      const concreteInput = materializeEdgeInput(edge.input, edge.match_type)
      walk(edge.destination, nextNodes, [...scripts, concreteInput])
    }
  }

  walk(flow.start_node, [], [])
  return paths
}

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32)

export const uniqueNodeId = (flow: IVRFlow, base = "node"): string => {
  const existing = new Set(flow.nodes.map((n) => n.id))
  const slug = slugify(base) || "node"
  if (!existing.has(slug)) {
    return slug
  }
  let i = 2
  while (existing.has(`${slug}_${i}`)) {
    i += 1
  }
  return `${slug}_${i}`
}

export const createEmptyFlow = (): IVRFlow => ({
  start_node: "greeting",
  match_threshold: 0.85,
  nodes: [
    {
      id: "greeting",
      prompt: "Thanks for calling.",
      terminal: true,
    },
  ],
  edges: [],
})

export const exampleFlow = (): IVRFlow => ({
  start_node: "greeting",
  match_threshold: 0.85,
  nodes: [
    {
      id: "greeting",
      prompt:
        "Thanks for calling. Please listen carefully as our menu options have changed.",
      auto_advance_to: "main_menu",
    },
    {
      id: "main_menu",
      prompt:
        "For account access, press 1. To open a new account, press 2. To speak with a representative, press 0, or say 'operator'.",
    },
    {
      id: "verify_phone",
      prompt:
        "Please enter your 10 digit phone number, followed by the pound key.",
    },
    {
      id: "verify_pin",
      prompt: "Please enter your 4 digit PIN.",
    },
    {
      id: "verification_failed",
      prompt:
        "We were unable to verify your identity. Press 1 to try again, or 0 to speak with a representative.",
    },
    {
      id: "account_menu",
      prompt:
        "For account balance, press 1. For recent transactions, press 2. To make a transfer, press 3. To return to the main menu, press 9.",
    },
    {
      id: "balance",
      prompt:
        "Your current account balance is available in your statement. Goodbye.",
      terminal: true,
    },
    {
      id: "recent_transactions",
      prompt:
        "Your five most recent transactions have been read aloud. Goodbye.",
      terminal: true,
    },
    {
      id: "transfer_amount",
      prompt:
        "Please enter the transfer amount in dollars, followed by the pound key.",
    },
    {
      id: "confirm_transfer",
      prompt: "Press 1 to confirm the transfer, or 2 to cancel.",
    },
    {
      id: "transfer_complete",
      prompt: "Your transfer has been scheduled. Goodbye.",
      terminal: true,
    },
    {
      id: "new_account_intake",
      prompt: "To open a new account, we'll connect you with a representative.",
      auto_advance_to: "transfer_to_agent",
    },
    {
      id: "transfer_to_agent",
      prompt:
        "Please hold while we transfer you to the next available representative.",
      terminal: true,
    },
  ],
  edges: [
    { source: "main_menu", input: "1", destination: "verify_phone" },
    { source: "main_menu", input: "2", destination: "new_account_intake" },
    { source: "main_menu", input: "0", destination: "transfer_to_agent" },
    {
      source: "main_menu",
      input: "operator",
      destination: "transfer_to_agent",
      match_type: "fuzzy",
    },
    {
      source: "verify_phone",
      input: "\\d{10}",
      destination: "verify_pin",
      match_type: "regex",
    },
    { source: "verify_phone", input: "0", destination: "transfer_to_agent" },
    {
      source: "verify_pin",
      input: "\\d{4}",
      destination: "account_menu",
      match_type: "regex",
    },
    { source: "verify_pin", input: "*", destination: "verification_failed" },
    {
      source: "verification_failed",
      input: "1",
      destination: "verify_phone",
    },
    {
      source: "verification_failed",
      input: "0",
      destination: "transfer_to_agent",
    },
    { source: "account_menu", input: "1", destination: "balance" },
    { source: "account_menu", input: "2", destination: "recent_transactions" },
    { source: "account_menu", input: "3", destination: "transfer_amount" },
    { source: "account_menu", input: "9", destination: "main_menu" },
    {
      source: "transfer_amount",
      input: "\\d{1,6}",
      destination: "confirm_transfer",
      match_type: "regex",
    },
    {
      source: "confirm_transfer",
      input: "1",
      destination: "transfer_complete",
    },
    { source: "confirm_transfer", input: "2", destination: "account_menu" },
  ],
})
